import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuItem,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Input,
  Skeleton,
} from "@pushpress/pushpress-ui";
import {
  BarChart,
  DollarSign,
  PartyPopper,
  TrendingUp,
  Users,
  UserX,
  Settings,
  RotateCcw,
  ArrowLeftFromLine,
  Search,
  X,
  ArrowUpRight,
  UserCheck,
} from "lucide-react";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import { useNavigate } from "../../../hooks/useNavigate";
import { useLegacyNavigate } from "../../../hooks/useLegacyNavigate";
import { checkIfHasAccessToRoute } from "../../../routes/util";
import { AppContext } from "../../../context/app";
import { useContext, useState, useEffect, useRef } from "react";
import { QuickAction } from "../../../apis/userApi";
import {
  useQuickActionsQuery,
  useToggleQuickActionMutation,
  useUpdateQuickActionPositionsMutation,
  useQuickActionsListQuery,
  useResetQuickActionsMutation,
} from "../../../apis/userApi.hooks";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import useTailwindBreakpoint from "../../../hooks/useTailwindBreakpoint";
import { SHOW_PEOPLE_PAGE_FEATURE_FLAG } from "../../../context/features/features.utils";
import { useExperiment } from "../../../hooks/useExperiment";
import { useDashboard } from "../DashboardProvider/DashboardProvider";
import { SortableQuickAction } from "./SortableQuickAction";

const MAX_QUICK_ACTIONS = 5;
const DEFAULT_QUICK_ACTION_POSITION = 10;
export const DEFAULT_QUICK_ACTION_ICON = ArrowUpRight;

export const iconMap = {
  MEMBERS: Users,
  REPORT_PAYMENTS_FORECAST: TrendingUp,
  REPORT_FINANCIAL: DollarSign,
  REPORT_NO_SHOW: UserX,
  REPORT_ENGAGEMENT: BarChart,
  REPORT_CELEBRATIONS: PartyPopper,
  CHECKIN_SIDE_PANEL: UserCheck,
};

const getNextPosition = (
  actions: QuickAction[] | undefined,
  selectedKeys: string[],
) => {
  if (!actions || actions.length === 0 || selectedKeys.length === 0) {
    return DEFAULT_QUICK_ACTION_POSITION;
  }

  const positions = actions
    .filter((action) => selectedKeys.includes(action.action))
    .map((action) => action.position)
    .sort((a, b) => a - b);

  if (positions.length === 0) {
    return DEFAULT_QUICK_ACTION_POSITION;
  }

  // Return the position after the last one (with 10 spacing)
  return positions[positions.length - 1] + DEFAULT_QUICK_ACTION_POSITION;
};
const QuickActionsSkeleton = () => {
  const { isXsScreen } = useTailwindBreakpoint();
  const numSkeletons = isXsScreen ? 2 : 5;

  return (
    <div className="tw-mb-6">
      <div className="tw-flex tw-justify-end tw-items-center tw-mb-2">
        <Skeleton className="tw-h-8 tw-w-8 tw-rounded" />
      </div>
      <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-flex lg:tw-flex-row tw-gap-4 tw-mb-6">
        {[...Array(numSkeletons)].map((_, index) => (
          <div key={index} className="tw-flex tw-w-full lg:tw-flex-1">
            <Skeleton className="tw-h-10 tw-w-full tw-rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  );
};

// Define the callback map type
type QuickActionCallback = () => void;

const QuickActions = () => {
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const legacyNavigate = useLegacyNavigate();
  const { user } = useContext(AppContext);
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [actionPositions, setActionPositions] = useState<
    Record<string, number>
  >({});
  const [showResetQuickActionsConfirm, setShowResetQuickActionsConfirm] =
    useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { openSheet } = useDashboard();

  const quickActionCallbackMap: Record<string, QuickActionCallback> = {
    checkin_sidepanel: () => {
      openSheet("classCheckin", "Checkin", { isMultiClass: true }, undefined);
    },
  };

  const {
    data: quickActions = [],
    isLoading,
    error: quickActionsError,
  } = useQuickActionsQuery();
  const {
    data: allQuickActionsForCurrentRole,
    isLoading: isLoadingList,
    error: quickActionsListError,
  } = useQuickActionsListQuery();
  const toggleQuickActionMutation = useToggleQuickActionMutation();
  const updatePositionsMutation = useUpdateQuickActionPositionsMutation();
  const resetQuickActionsMutation = useResetQuickActionsMutation();

  // TODO: Remove this once the people list page is GA'd
  const { useExperimentValue } = useExperiment();
  const isPeoplePageEnabled = useExperimentValue(
    SHOW_PEOPLE_PAGE_FEATURE_FLAG,
    false,
  ) as boolean;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // add a new state to track if we're showing quick actions
  const [showQuickActions, setShowQuickActions] = useState<boolean>(true);

  // focus search input when dropdown opens
  useEffect(() => {
    if (searchInputRef.current) {
      // small delay to ensure the dropdown is fully rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, []);

  // clear search when dropdown closes
  const handleDropdownOpenChange = (open: boolean) => {
    if (!open) {
      setSearchQuery("");
    }
  };

  useEffect(() => {
    // Skip the effect entirely if there's an error
    if (quickActionsError) {
      return;
    }

    if (quickActions) {
      const backendActions = quickActions.map((action) => action.action);

      // Only update selectedActions if they're different from current state
      if (backendActions.length > 0 && !selectedActions.length) {
        setSelectedActions(backendActions);
        setShowQuickActions(true);
      } else if (backendActions.length === 0 && selectedActions.length > 0) {
        setSelectedActions([]);
        setShowQuickActions(false);
      }

      // Only update positions if they've changed
      const positionsMap: Record<string, number> = {};
      quickActions.forEach((action) => {
        positionsMap[action.action] = action.position;
      });

      // Compare current positions with new positions before updating
      const hasPositionChanges = Object.entries(positionsMap).some(
        ([action, position]) => actionPositions[action] !== position,
      );

      if (hasPositionChanges) {
        setActionPositions(positionsMap);
      }
    }
  }, [quickActions, quickActionsError]);

  // Handle navigation based on action type and path
  const handleActionClick = (quickAction: QuickAction) => {
    trackEvent("Quick Action Clicked", {
      quick_action: quickAction.label,
      position: quickAction.position,
    });

    // TODO: Remove this once the people list page is GA'd
    if (isPeoplePageEnabled) {
      if (quickAction.value === "MEMBERS") {
        navigate("/people/members");
        return;
      } else if (quickAction.value === "LEADS") {
        navigate("/people/leads");
        return;
      } else if (quickAction.value === "STAFF") {
        navigate("/people/staff");
        return;
      }
    }

    let callback: QuickActionCallback | undefined;
    // Handle navigation based on the action type
    switch (quickAction.type) {
      case "legacy_link":
        legacyNavigate(quickAction.action);
        break;
      case "external_link":
        window.open(quickAction.action, "_blank");
        break;
      case "callback":
        callback = quickActionCallbackMap[quickAction.action];
        if (callback) {
          callback();
        } else {
          console.warn(`No callback found for action: ${quickAction.action}`);
        }
        break;
      case "link":
      default:
        navigate(quickAction.action);
        break;
    }
  };

  // handle resetting to default actions
  const handleResetToDefaults = async () => {
    if (quickActions) {
      // Get default actions
      const defaultActions = quickActions
        .filter((action) => action.isDefault)
        .map((action) => action.action);

      try {
        // Track the reset event
        trackEvent("Quick Actions Reset", {
          count: defaultActions.length,
        });

        await resetQuickActionsMutation.mutateAsync().then(() => {
          window.location.reload();
        });
      } catch (error) {
        trackEvent("Quick Action Error", {
          error: "Error resetting quick actions",
        });
      } finally {
        setShowResetQuickActionsConfirm(false);
      }
    }
  };

  // Handle toggling quick action selection and update positions
  const handleActionToggle = async (
    quickAction: QuickAction,
    checked: boolean,
  ) => {
    if (checked) {
      const cannotAddQuickAction = selectedActions.length >= MAX_QUICK_ACTIONS;
      if (cannotAddQuickAction) {
        return;
      }

      // When adding a new action, calculate the next available position
      const newPosition = getNextPosition(quickActions, selectedActions);

      // Optimistically update local state
      const newSelectedActions = [...selectedActions, quickAction.action];
      setSelectedActions(newSelectedActions);
      setActionPositions((prev) => ({
        ...prev,
        [quickAction.action]: newPosition,
      }));

      // Make sure quick actions are shown (must happen after state is updated)
      setShowQuickActions(true);

      // Persist the change via API and wait for it to complete
      try {
        await toggleQuickActionMutation.mutateAsync({
          uuid: quickAction.uuid,
          operation: "add",
        });
      } catch (error) {
        // Revert optimistic updates on error
        setSelectedActions(
          selectedActions.filter((key) => key !== quickAction.action),
        );
        setShowQuickActions(selectedActions.length > 0);
      }
    } else {
      // When removing an action, remove it from the selected list
      const newSelected = selectedActions.filter(
        (key) => key !== quickAction.action,
      );

      // Update UI state based on whether there are any actions left
      const willBeEmpty = newSelected.length === 0;

      // Find the client user quick action UUID from the current quick actions
      const currentAction = quickActions?.find(
        (action) => action.action === quickAction.action,
      );

      if (!currentAction) {
        return;
      }

      // Optimistically update UI
      setSelectedActions(newSelected);

      if (willBeEmpty) {
        setShowQuickActions(false);
      }

      // Persist the removal via API
      try {
        await toggleQuickActionMutation.mutateAsync({
          uuid: currentAction.uuid,
          operation: "remove",
        });
      } catch (error) {
        // Revert optimistic updates on error
        setSelectedActions([...selectedActions]);
        setShowQuickActions(true);
      }
    }

    // Track the toggle event
    trackEvent("Quick Action Toggled", {
      quick_action: quickAction.action,
      state: checked ? "selected" : "deselected",
    });
  };

  // handle drag end for reordering
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Get the current enabled actions
      const currentActions = enabledActions || [];

      // Find indices for the dragged item and the drop target
      const oldIndex = currentActions.findIndex(
        (item) => item.action === active.id,
      );
      const newIndex = currentActions.findIndex(
        (item) => item.action === over.id,
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        // Create a new array with the updated order
        const newOrderedActions: QuickAction[] = arrayMove(
          currentActions,
          oldIndex,
          newIndex,
        );

        // Update positions based on the new order
        const updatedPositions = { ...actionPositions };
        newOrderedActions.forEach((action: QuickAction, index: number) => {
          // use a gap of 10 between positions for easier future insertions
          updatedPositions[action.action] = (index + 1) * 10;
        });

        setActionPositions(updatedPositions);

        // track the reordering event
        trackEvent("Quick Actions Reordered", {
          moved_action: currentActions[oldIndex].label,
          from_position: oldIndex,
          to_position: newIndex,
        });

        // save the new order to the backend
        saveQuickActionsOrder(newOrderedActions, updatedPositions);
      }
    }
  };

  // function to save the updated quick action order to the backend
  const saveQuickActionsOrder = async (
    actions: QuickAction[],
    positions: Record<string, number>,
  ) => {
    try {
      const actionsForApi = actions.map((action) => ({
        uuid: action.uuid,
        position: positions[action.action],
      }));

      await updatePositionsMutation.mutateAsync({
        actions: actionsForApi,
      });
    } catch (error) {
      trackEvent("Quick Action Error", {
        error: "Error saving quick action order",
      });
    }
  };

  if (isLoading || isLoadingList) {
    return <QuickActionsSkeleton />;
  }

  // Filter actions based on role and route access for the selected actions
  const filteredActions = quickActions
    ?.filter((quickAction: QuickAction) => {
      const routeTypes = ["legacy_link", "link"];
      const isRouteType = routeTypes.includes(quickAction.type);

      if (isRouteType) {
        return checkIfHasAccessToRoute(user, quickAction.action);
      }

      return true;
    })
    .sort((a, b) => a.position - b.position);

  // Get only the enabled actions for the grid view
  const enabledActions = filteredActions
    ?.filter((action) => selectedActions.includes(action.action))
    .sort((a, b) => {
      const posA = actionPositions[a.action] || a.position;
      const posB = actionPositions[b.action] || b.position;
      return posA - posB;
    });

  // Filter all actions for the dropdown based on both search query and role/access
  const dropdownActions = allQuickActionsForCurrentRole
    ?.filter((quickAction) => {
      const routeTypes = ["legacy_link", "link"];
      const isRouteType = routeTypes.includes(quickAction.type);

      if (isRouteType) {
        return checkIfHasAccessToRoute(user, quickAction.action);
      }

      return true;
    })
    .filter((action) => {
      if (!searchQuery) return true;
      return action.label.toLowerCase().includes(searchQuery.toLowerCase());
    })
    .sort((a, b) => {
      // First sort by selected status
      const aSelected = selectedActions.includes(a.action);
      const bSelected = selectedActions.includes(b.action);
      if (aSelected !== bSelected) {
        return aSelected ? -1 : 1;
      }
      // Then sort alphabetically
      return a.label.localeCompare(b.label);
    });

  return (
    <div className="tw-mb-6">
      <div className="tw-flex tw-justify-end tw-items-center tw-mb-2">
        <DropdownMenu onOpenChange={handleDropdownOpenChange}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="tw-text-gray-500 hover:tw-bg-gray-200 hover:tw-text-gray-700 tw-border-none focus-visible:tw-ring-transparent"
            >
              <Settings className="tw-w-3.5 tw-h-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="tw-w-64 tw-max-h-[500px] tw-overflow-hidden tw-flex tw-flex-col"
            align="end"
          >
            {quickActionsError || quickActionsListError ? (
              <div className="tw-py-2 tw-px-3 tw-text-center tw-text-gray-500 tw-text-xs">
                Error loading quick actions
              </div>
            ) : (
              <>
                <div className="tw-px-2 tw-pt-2 tw-pb-1">
                  <div
                    role="search"
                    className="tw-relative"
                    aria-label="Search quick actions"
                  >
                    <Search className="tw-absolute tw-left-2 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-w-3.5 tw-h-3.5 tw-text-gray-400" />
                    <Input
                      ref={searchInputRef}
                      placeholder="Search quick actions..."
                      value={searchQuery}
                      onChange={(e) => {
                        // prevent event bubbling
                        e.stopPropagation();
                        const newValue = e.target.value;
                        setSearchQuery(newValue);
                      }}
                      onKeyDown={(e) => {
                        // Prevent keyboard events from bubbling
                        e.stopPropagation();
                      }}
                      className="tw-pl-8 tw-h-8 tw-text-xs"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="tw-absolute tw-right-2 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600"
                      >
                        <X className="tw-w-3.5 tw-h-3.5" />
                      </button>
                    )}
                  </div>
                </div>

                <DropdownMenuSeparator />

                {/* Scrollable area for quick action options */}
                <div className="tw-overflow-y-auto tw-flex-1">
                  {dropdownActions?.length ? (
                    <>
                      {/* Selected Actions Section */}
                      {selectedActions.length > 0 && (
                        <>
                          <div className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-500 tw-bg-gray-50 tw-flex tw-justify-between tw-items-center">
                            <span>Selected Quick Actions</span>
                            <span
                              className={`${selectedActions.length === MAX_QUICK_ACTIONS ? "tw-text-gray-700 tw-font-medium" : ""}`}
                            >
                              {selectedActions.length}/{MAX_QUICK_ACTIONS}
                            </span>
                          </div>
                          {dropdownActions
                            .filter((action) =>
                              selectedActions.includes(action.action),
                            )
                            .map((quickAction) => {
                              const IconComponent =
                                iconMap[
                                  quickAction.value as keyof typeof iconMap
                                ] ?? DEFAULT_QUICK_ACTION_ICON;
                              return (
                                <DropdownMenuCheckboxItem
                                  key={quickAction.action}
                                  checked={true}
                                  onCheckedChange={(checked) => {
                                    return handleActionToggle(
                                      quickAction,
                                      false,
                                    ).catch((err) => {
                                      trackEvent("Quick Action Error", {
                                        error: "Error toggling quick action",
                                        error_message: err.message,
                                        quick_action: quickAction.action,
                                      });
                                    });
                                  }}
                                  onSelect={(e) => {
                                    e.preventDefault();
                                  }}
                                >
                                  <div className="tw-flex tw-items-center tw-gap-2">
                                    <IconComponent className="tw-w-4 tw-h-4 tw-flex-shrink-0 tw-text-gray-500" />
                                    <p className="tw-truncate tw-text-gray-600 tw-text-sm">
                                      {quickAction.label}
                                    </p>
                                  </div>
                                </DropdownMenuCheckboxItem>
                              );
                            })}
                          <DropdownMenuSeparator />
                        </>
                      )}

                      {/* Available Actions Section */}
                      <div className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-500 tw-bg-gray-50">
                        Available Quick Actions
                      </div>
                      {dropdownActions
                        .filter(
                          (action) => !selectedActions.includes(action.action),
                        )
                        .map((quickAction) => {
                          const isDisabled =
                            selectedActions.length >= MAX_QUICK_ACTIONS;
                          const IconComponent =
                            iconMap[
                              quickAction.value as keyof typeof iconMap
                            ] ?? DEFAULT_QUICK_ACTION_ICON;
                          return (
                            <DropdownMenuCheckboxItem
                              key={quickAction.action}
                              checked={false}
                              disabled={isDisabled}
                              onCheckedChange={(checked) => {
                                return handleActionToggle(
                                  quickAction,
                                  true,
                                ).catch((err) => {
                                  trackEvent("Quick Action Error", {
                                    error: "Error toggling quick action",
                                    error_message: err.message,
                                    quick_action: quickAction.action,
                                  });
                                });
                              }}
                              onSelect={(e) => {
                                e.preventDefault();
                              }}
                            >
                              <div
                                className={`tw-flex tw-items-center tw-gap-2 ${isDisabled ? "tw-opacity-50" : ""}`}
                              >
                                <IconComponent className="tw-w-4 tw-h-4 tw-flex-shrink-0 tw-text-gray-500" />
                                <p className="tw-truncate tw-text-gray-600 tw-text-sm">
                                  {quickAction.label}
                                </p>
                              </div>
                            </DropdownMenuCheckboxItem>
                          );
                        })}
                    </>
                  ) : (
                    <div className="tw-py-2 tw-px-3 tw-text-center tw-text-gray-500 tw-text-xs">
                      No quick actions match your search
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Sticky footer section */}
            {!quickActionsError && !quickActionsListError && (
              <div className="tw-sticky tw-bottom-0 tw-bg-white">
                <DropdownMenuSeparator className="tw-m-0" />
                <DropdownMenuItem
                  className="tw-text-gray-500 tw-text-xs hover:tw-text-red-600 hover:tw-bg-red-50"
                  onClick={() => setShowResetQuickActionsConfirm(true)}
                >
                  <RotateCcw className="tw-w-3 tw-h-3 tw-mr-2" />
                  Reset quick actions to default
                </DropdownMenuItem>
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Reset confirmation dialog */}
      <AlertDialog
        open={showResetQuickActionsConfirm}
        onOpenChange={setShowResetQuickActionsConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Quick Actions</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset your quick actions to the default settings. Your
              customizations will be lost. Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="tw-bg-red-500 hover:tw-bg-red-600"
              onClick={handleResetToDefaults}
            >
              Reset Quick Actions
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Grid of quick action buttons - only show if showQuickActions is true */}
      {showQuickActions && (enabledActions?.length ?? 0) > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={enabledActions?.map((a) => a.action) ?? []}
            strategy={horizontalListSortingStrategy}
          >
            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-flex lg:tw-flex-row tw-gap-4 tw-mb-6">
              {enabledActions?.map((quickAction) => (
                <SortableQuickAction
                  key={quickAction.action}
                  quickAction={quickAction}
                  onActionClick={handleActionClick}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default QuickActions;
